// Wave 19.1 — Free-tier "time remaining this week" banner.
//
// Sits at the top of every authenticated page on aero.chat (never on
// rapha.aero.chat). Displays the rider's remaining live-session minutes
// for the current rolling 7-day window and a one-click upgrade CTA that
// opens the PaywallModal.
//
// Data source: useSubscription().usage — populated on mount by GET
// /subscriptions/me/usage and refreshed every heartbeat during a live
// session (piggyback path in useVisionPipeline). Between heartbeats the
// display is static; we don't interpolate locally because that would
// tick down on pages where the rider isn't actually using time.
//
// Hidden entirely when:
//   - the rider is on rapha.aero.chat (isRaphaDomain())
//   - user.role === 'rapha'
//   - no usage snapshot yet (still loading)
//   - usage.isFree is false (Pro / legacy Pro)
//   - not authenticated (readCurrentUser().isAuthenticated === false)

import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSubscription } from '../services/useSubscription';
import { readCurrentUser } from '../services/useCurrentUser';
import { isRaphaDomain } from '../services/subdomain';
import { colors } from '../theme';
import PaywallModal from './PaywallModal';

function daysUntil(iso: string): number {
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((d - now) / 86_400_000));
  } catch {
    return 0;
  }
}

function resetLabel(iso: string): string {
  const d = daysUntil(iso);
  if (d === 0) return 'Resets today';
  if (d === 1) return 'Resets tomorrow';
  return `Resets in ${d} days`;
}

export default function FreeTierBanner() {
  const { usage } = useSubscription();
  const user = readCurrentUser();
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Guards — order matters; cheap checks first.
  if (isRaphaDomain()) return null;
  if (!user.isAuthenticated) return null;
  if (user.role === 'rapha') return null;
  if (!usage) return null;
  if (!usage.isFree) return null;

  const usedSec = usage.weeklyUsedSec ?? 0;
  const limitSec = usage.weeklyLimitSec ?? 0;
  const remainingSec = Math.max(0, limitSec - usedSec);
  const remainingMin = Math.ceil(remainingSec / 60);
  const depleted = remainingSec <= 0;
  const low = !depleted && remainingMin <= 5;

  const bg = depleted
    ? `${colors.danger}18`
    : low
    ? `${colors.warning}18`
    : `${colors.brand}12`;
  const border = depleted
    ? colors.danger
    : low
    ? colors.warning
    : `${colors.brand}66`;
  const fg = depleted ? colors.danger : low ? colors.warning : colors.brand;

  const message = depleted
    ? 'Weekly free-tier limit reached'
    : low
    ? `${remainingMin} min left this week — running low`
    : `${remainingMin} min of live-session time left this week`;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, md: 4 },
          py: 1.25,
          backgroundColor: bg,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <AccessTimeIcon sx={{ color: fg, fontSize: 18 }} />
          <Typography sx={{ color: fg, fontWeight: 600, fontSize: '0.875rem' }}>
            {message}
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
            · {resetLabel(usage.weeklyResetAt)}
          </Typography>
        </Box>
        <Button
          onClick={() => setPaywallOpen(true)}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: fg,
            color: colors.textInverse,
            fontWeight: 700,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: fg, opacity: 0.9 },
          }}
        >
          Upgrade to Pro
        </Button>
      </Box>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason="weekly_cap"
      />
    </>
  );
}
